import { useCallback, useMemo, useState, useTransition } from 'react';
import type { Selection } from 'react-aria-components';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { enableViewCreationFromSelect, enableViewFavoriting, enableViews } from '@gonfalon/dogfood-flags/dogfood-flags';
import { ContextualFeedbackButton } from '@gonfalon/feedback/ContextualFeedbackButton';
import type { UIFlagFilter } from '@gonfalon/flag-filters/types';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { AccessCheck } from '@gonfalon/permissions/access-check';
import { useLatestRef } from '@gonfalon/react/useLatestRef';
import { getProjectQuery } from '@gonfalon/rest-api/queries/projects';
import { getViewsQuery } from '@gonfalon/rest-api/queries/views';
import { SearchField } from '@gonfalon/ui/Form';
import { TextTruncator } from '@gonfalon/ui/TextTruncator';
import { Box } from '@launchpad-ui/box';
import {
  Button,
  ButtonGroup,
  Dialog,
  IconButton,
  Menu,
  MenuItem,
  ProgressBar,
  snackbarQueue,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { uniqBy } from 'lodash';

import type { ViewSelectedResource } from '../../components/AddResourcesToViewModal';
import { fetchCanLinkResourceToView, useViewLinkAccessByKey } from '../../linkAccess';
import type { LinkableResourceType, View } from '../../types';
import { getHumanLinkableResourceType } from '../../utils';
import { useViewCreation } from '../../ViewCreationContext';
import { VIEWS_QUERY_STALE_TIME } from '../constants';

import { ProjectEmptyViewsState } from './ProjectEmptyViewsState';
import { RichViewsDialog } from './RichViewsDialog';

import styles from './ViewsDialog.module.css';

export type ViewsDialogProps = {
  onSelectionChange: (views: View[]) => void;
  selectedViews: View[];
  label?: string;
  selectionMode?: 'single' | 'multiple';
  allowClear?: boolean;
  onBeforeCreateView?: () => void;
  onFeedbackPress?: () => void;
  onExpandDetails?: () => void;
  flagFilters?: UIFlagFilter;
  resourceType?: LinkableResourceType;
  initialSelectedResources?: Array<ViewSelectedResource<LinkableResourceType>>;
  hideCreateAndClose?: boolean;
  /**
   * When true, selecting/deselecting a view row links/unlinks a `resourceType`
   * resource on the backend (REL-15125), so rows the member may not
   * link/unlink render disabled with an explanation, keyed off each view's
   * `_access`. Leave unset in contexts where selection doesn't mutate view
   * links — filters, and creation-time association (which the backend
   * intentionally allows with only `viewView` access).
   */
  enforceLinkAccess?: boolean;
  /**
   * When true, suppress the footer `Create view` affordance. Useful in filter
   * contexts where inline view creation doesn't make sense (e.g. the SDK Keys
   * view filter).
   */
  hideCreateButton?: boolean;
  /**
   * Project key to scope the views query against. When omitted, falls back to the active
   * `useProjectContext()`. Required when this dialog is mounted outside of the project-context
   * route loader (e.g. `NewSdkKeyModal` at `/settings/sdk-keys`).
   */
  projectKey?: string;
};

export function ViewsDialog(props: ViewsDialogProps) {
  if (enableViewFavoriting()) {
    const {
      onSelectionChange,
      selectedViews = [],
      label = 'Select views',
      selectionMode = 'multiple',
      onBeforeCreateView,
      flagFilters,
      resourceType,
      initialSelectedResources,
      hideCreateAndClose,
      enforceLinkAccess,
      hideCreateButton,
      projectKey,
    } = props;

    return (
      <Dialog aria-label={label} className={styles.compactRichDialog}>
        <RichViewsDialog
          onSelectionChange={onSelectionChange}
          selectedViews={selectedViews}
          label={label}
          selectionMode={selectionMode}
          onBeforeCreateView={onBeforeCreateView}
          flagFilters={flagFilters}
          resourceType={resourceType}
          initialSelectedResources={initialSelectedResources}
          hideCreateAndClose={hideCreateAndClose}
          enforceLinkAccess={enforceLinkAccess}
          isExpanded={false}
          hideSelectionFooter
          hideCreateButton={hideCreateButton}
          projectKey={projectKey}
        />
      </Dialog>
    );
  }

  return <LegacyViewsDialog {...props} />;
}

function LegacyViewsDialog({ projectKey: projectKeyProp, ...props }: ViewsDialogProps) {
  // Same fallback rationale as `RichViewsDialog` — see its top-of-function comment for context.
  const projectContext = useProjectContext({ optional: true });
  const projectKey = projectKeyProp ?? projectContext?.project.key;
  // Resolve `projectKey` and guard here — before any other hooks run — so the conditional throw can
  // never change the hook count across renders. The hook-heavy body lives in `LegacyViewsDialogContent`.
  if (!projectKey) {
    throw new Error(
      'ViewsDialog requires a `projectKey` prop when rendered outside of the project-context route loader.',
    );
  }
  // Only forward the context project when the caller didn't override `projectKey` — see
  // `RichViewsDialog` for the rationale (avoids handing a mismatched project to `AccessCheck`).
  return (
    <LegacyViewsDialogContent
      {...props}
      projectKey={projectKey}
      contextProject={projectKeyProp ? undefined : projectContext?.project}
    />
  );
}

type LegacyViewsDialogContentProps = Omit<ViewsDialogProps, 'projectKey'> & {
  projectKey: string;
  contextProject?: NonNullable<ReturnType<typeof useProjectContext>>['project'];
};

function LegacyViewsDialogContent({
  onSelectionChange,
  selectedViews = [],
  label = 'Select views',
  selectionMode = 'multiple',
  allowClear,
  onBeforeCreateView,
  onFeedbackPress,
  onExpandDetails,
  flagFilters,
  resourceType,
  initialSelectedResources,
  hideCreateAndClose = false,
  enforceLinkAccess = false,
  hideCreateButton = false,
  projectKey,
  contextProject,
}: LegacyViewsDialogContentProps) {
  const [filterText, setFilterText] = useState('');
  const [isTransitioning, startTransition] = useTransition();
  const queryClient = useQueryClient();
  // `project` is required by downstream `AccessCheck` wrappers. Inside the project-context loader
  // we already have it from `contextProject`; outside (e.g. `NewSdkKeyModal` at `/settings/sdk-keys`)
  // we suspense-fetch from the resolved `projectKey`. The query is cached by React Query, so when
  // the project context already populated it (in-loader path) this is effectively free.
  const { data: fetchedProject } = useSuspenseQuery(getProjectQuery({ projectKey }));
  const project = contextProject ?? fetchedProject;

  const { openCreateModal, setOnViewCreated } = useViewCreation();
  const isCreateViewFeatureEnabled = enableViews() && enableViewCreationFromSelect() && !hideCreateButton;
  const canRenderFeedback = Boolean(onFeedbackPress);
  // `shouldRenderFooterCreate` is recomputed later once we know if the project
  // is empty (to avoid duplicate CTA with `ProjectEmptyViewsState`).

  // Use refs to avoid stale closures in the callback passed to setOnViewCreated
  const selectedViewsRef = useLatestRef(selectedViews);
  const onSelectionChangeRef = useLatestRef(onSelectionChange);

  const handleCreateClick = useCallback(() => {
    onBeforeCreateView?.();
    setOnViewCreated((view) => {
      // REL-15125: in linking contexts, auto-selecting the just-created view
      // commits a real link, and a member can hold `createView` without the
      // link action. Verify against the access-check endpoint and skip the
      // auto-link (the view itself was still created) with an explanation,
      // instead of letting the commit 403.
      if (enforceLinkAccess && resourceType !== undefined) {
        void fetchCanLinkResourceToView(queryClient, { project, resourceType, view }).then(
          ({ isAllowed, failedToCheck }) => {
            if (!isAllowed) {
              const noun = getHumanLinkableResourceType(resourceType).lower;
              snackbarQueue.add({
                description: failedToCheck
                  ? `View created, but we couldn't verify your permissions to link ${noun}s to it`
                  : `View created, but your permissions don't allow you to link ${noun}s to it`,
              });
              return;
            }
            onSelectionChangeRef.current([...selectedViewsRef.current, view]);
          },
        );
        return;
      }
      onSelectionChangeRef.current([...selectedViewsRef.current, view]);
    });
    if (hideCreateAndClose) {
      openCreateModal(filterText, flagFilters, resourceType, initialSelectedResources, { hideCreateAndClose });
    } else {
      openCreateModal(filterText, flagFilters, resourceType, initialSelectedResources);
    }
  }, [
    filterText,
    flagFilters,
    resourceType,
    initialSelectedResources,
    hideCreateAndClose,
    enforceLinkAccess,
    queryClient,
    project,
    onBeforeCreateView,
    openCreateModal,
    setOnViewCreated,
  ]);

  const { data: viewsRes, isPending } = useSuspenseQuery({
    ...getViewsQuery({
      projectKey,
      query: filterText ? { filter: { query: filterText } } : undefined,
      header: { 'LD-API-Version': 'beta' },
    }),
    staleTime: VIEWS_QUERY_STALE_TIME,
  });

  // Views selected when the dialog opened sort first; the order is a mount-time snapshot so that
  // selecting an item never reorders the open menu under keyboard/screen-reader users (REL-15082).
  // The dialog remounts on each popover open, refreshing the snapshot. Live `selectedViews` are
  // still merged in (at the end) so every selected key resolves in `handleSelectionChange` and
  // stays removable even when the current search no longer returns it.
  const [viewsSelectedOnOpen] = useState(selectedViews);
  const allViews = useMemo(
    () => uniqBy([...viewsSelectedOnOpen, ...(viewsRes?.items ?? []), ...selectedViews], 'key'),
    [viewsRes, selectedViews, viewsSelectedOnOpen],
  );

  const isLoading = isTransitioning || isPending;

  const handleFilterChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };

  const handleSelectionChange = (selection: Selection) => {
    if (typeof selection === 'string') {
      return;
    }

    const keys = Array.from(selection);
    if (keys.length > 0) {
      const views = keys.map((key) => allViews.find((v) => v.key === key)).filter((v) => !!v);
      onSelectionChange(views);
      return;
    }

    // nothing is selected
    onSelectionChange([]);
  };

  // REL-15125: in linking contexts, selecting a row links a resource to the
  // view and deselecting unlinks it — both enforced per view on the backend.
  // Disable rows whose (un)link the member's role denies, using the direction
  // the toggle would fire. Checks go through the access-check endpoint
  // (batched + cached); rows stay locked while their check is in flight, with
  // the explanation held back until a real denial resolves.
  const selectedViewKeySet = useMemo(() => new Set(selectedViews.map((selected) => selected.key)), [selectedViews]);
  const getViewLinkAccess = useViewLinkAccessByKey({
    isEnabled: enforceLinkAccess,
    project,
    resourceType,
    views: allViews,
    selectedViewKeys: selectedViewKeySet,
  });

  const renderViewMenuItem = (view: View) => {
    const rowLinkAccess = getViewLinkAccess(view.key);
    return (
      <MenuItem
        key={view.key}
        textValue={view.name}
        className={styles.viewMenuItem}
        isDisabled={!rowLinkAccess.isAllowed}
      >
        <Text slot="label">
          <div className={styles.viewMenuItemLabel}>
            <Icon name="stack" size="small" className={styles.viewNameIcon} />
            <TextTruncator>{view.name}</TextTruncator>
          </div>
        </Text>
        {rowLinkAccess.lockedReason !== undefined && <Text slot="description">{rowLinkAccess.lockedReason}</Text>}
      </MenuItem>
    );
  };

  const selectedKeys = selectedViews.map((view) => view.key);
  const isProjectEmpty = !isLoading && filterText.trim() === '' && allViews.length === 0;
  // Suppress the footer Create CTA when `ProjectEmptyViewsState` renders — the
  // empty state already owns the primary CTA, so avoid a duplicate.
  const shouldRenderFooterCreate = isCreateViewFeatureEnabled && !isProjectEmpty;
  const canRenderFooter = shouldRenderFooterCreate || allowClear || canRenderFeedback || !!onExpandDetails;
  const showPrimaryFooterRow = shouldRenderFooterCreate || !!onExpandDetails;

  const emptyState = () => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <ProgressBar aria-label={msg('views.status.loading')} isIndeterminate size="small" />
        </Box>
      );
    }
    if (filterText.trim() !== '') {
      // Search no-match — keep existing behavior.
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <>
            <Message id="views.viewsDialog.noViewsFound" />
          </>
        </Box>
      );
    }
    // Project has zero views — render the centered block with Create CTA.
    // Mirror the header/footer gating: suppress the empty-state Create button
    // whenever the create-view feature itself is disabled (feature flags off,
    // or caller passed `hideCreateButton`). Otherwise we'd render a Create CTA
    // here that the rest of the dialog intentionally hides.
    return <ProjectEmptyViewsState onCreateView={handleCreateClick} hideCreateButton={!isCreateViewFeatureEnabled} />;
  };

  return (
    <Dialog aria-label={label}>
      <SearchField
        autoFocus
        aria-label={msg('views.viewsDialog.searchLabel')}
        isBusy={isTransitioning}
        onChange={handleFilterChange}
        placeholder={msg('views.viewsDialog.searchLabel')}
      />
      <div className={styles.viewsList}>
        <Menu
          // React Aria caches rendered rows by item identity; without this,
          // rows would freeze at their first-render (locked-while-loading)
          // access state and never re-enable once the async checks resolve.
          dependencies={[getViewLinkAccess]}
          selectionMode={selectionMode}
          items={allViews}
          selectedKeys={selectedKeys}
          aria-label={msg('views.viewsDialog.selectLabel')}
          onSelectionChange={handleSelectionChange}
          renderEmptyState={emptyState}
        >
          {renderViewMenuItem}
        </Menu>
      </div>

      {canRenderFooter && (
        <Box display="flex" className={styles.footer} gap="$200">
          {showPrimaryFooterRow && (
            <div className={styles.footerPrimaryRow}>
              {shouldRenderFooterCreate && (
                <AccessCheck resourceType="view" action="createView" project={project} hideControlIfNotAllowed>
                  <div className={`${styles.createView} ${onExpandDetails ? styles.createViewWithSibling : ''}`.trim()}>
                    <Button variant="minimal" size="small" onPress={handleCreateClick}>
                      <Icon name="add" size="small" />
                      <span>{filterText.trim() ? `Create "${filterText.trim()}"` : 'Create view'}</span>
                    </Button>
                  </div>
                </AccessCheck>
              )}
              {onExpandDetails && (
                <TooltipTrigger>
                  <IconButton
                    aria-label={msg('views.viewsDialog.expandDetails')}
                    icon="chevrons-right"
                    size="small"
                    variant="minimal"
                    className={styles.expandDetailsButton}
                    onPress={onExpandDetails}
                  />
                  <Tooltip>
                    <Message id="views.viewsDialog.expandDetails" />
                  </Tooltip>
                </TooltipTrigger>
              )}
            </div>
          )}
          {(allowClear || canRenderFeedback) && (
            <Box>
              <ButtonGroup spacing="basic">
                {allowClear && (
                  <Button size="small" onClick={() => onSelectionChange([])}>
                    {msg('views.viewsDialog.clear')}
                  </Button>
                )}
                {canRenderFeedback && <ContextualFeedbackButton size="small" onPress={onFeedbackPress} isCompact />}
              </ButtonGroup>
            </Box>
          )}
        </Box>
      )}
    </Dialog>
  );
}
